import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useParallax } from "react-scroll-parallax";
import Testimonials2 from "../blocks/home/Testimonials2";
import workCategories from "../data/portfolio/workCategories";
import bannerImages from "../data/bannerImages.json";
import ResponsiveImage from "../components/ResponsiveImage";
import useAnimationFrameCallback from "../hooks/useAnimationFrameCallback";


const slideImages = [
  { name: "family", alt: "Family portrait outdoors in warm evening light", position: "50% 55%" },
  { name: "wedding", alt: "Bride and groom beside a white sports car", position: "55% 65%", mobilePosition: "70% 65%" },
  { name: "twilight-home", alt: "Home exterior beneath a colorful sunset sky", position: "50% 65%" },
  { name: "portrait", alt: "Refined portrait of a woman in a white dress", position: "50% 8%" },
  { name: "newborn", alt: "Sleeping newborn resting on a soft green blanket", position: "50% 35%" }
];

function getEditorialMotionMode() {
 if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return "reduced";
 return window.matchMedia("(max-width: 768px)").matches ? "mobile" : "desktop";
}

function CollagePhoto({ category, index, mobileMotion }) {
 const updateMobileMotion = useCallback(({ el, progress }) => {
   if (!mobileMotion || getEditorialMotionMode() !== "mobile") return;
   // Measure the stationary grid item; move its photo and caption together inside it.
   const distance = index % 2 === 0 ? 10 : 12;
   const boundedProgress = Math.max(0, Math.min(1, progress));
   el.style.setProperty("--rg-mobile-drift", `${(1 - boundedProgress * 2) * distance}px`);
   el.classList.add("is-visible");
 }, [index, mobileMotion]);
 const scheduleMobileMotion = useAnimationFrameCallback(updateMobileMotion);
 const { ref } = useParallax({ disabled: !mobileMotion, onChange: scheduleMobileMotion });

 useEffect(() => {
   const el = ref.current;
   if (!mobileMotion || !el) return;
   el.classList.add("rg-mobile-photo-ready");
   return () => {
     el.classList.remove("rg-mobile-photo-ready", "is-visible");
     el.style.removeProperty("--rg-mobile-drift");
   };
 }, [mobileMotion, ref]);

 const image = category.cover || category.images[0];
 return <div ref={ref} className={`rg-scroll-story-image rg-scroll-story-image-${index + 1}`}>
  <Link className="rg-work-link" to={`/work/${category.slug}`} aria-label={`View ${category.title.toLowerCase()} work`}>
   <ResponsiveImage
    src={image.src}
    srcSet={image.srcSet}
    sizes="(max-width: 768px) 45vw, 30vw"
    width={image.width}
    height={image.height}
    alt={image.alt}
    loading="lazy"
    defer
    decoding="async"
   />
   <span className="rg-scroll-story-photo-label">{category.title}</span>
  </Link>
 </div>;
}

export default function Home() {
 const [motionMode, setMotionMode] = useState(getEditorialMotionMode);
 const updateEditorialMotion = useCallback(({ el }) => {
   // A queued desktop update can arrive while the viewport or preference changes.
   if (getEditorialMotionMode() !== "desktop") {
     el.classList.remove("rg-text-viewport-pinned");
     return;
   }
   const rect = el.getBoundingClientRect();
   const viewportHeight = window.innerHeight;
   const scrollable = Math.max(1, rect.height - viewportHeight);
   const progress = Math.max(0, Math.min(1, -rect.top / scrollable));
   const entry = Math.max(0, Math.min(1, (viewportHeight - rect.top) / (viewportHeight * 0.62)));
   el.style.setProperty("--rg-scroll-progress", progress);
   el.style.setProperty("--rg-entry-progress", entry);
   el.style.setProperty("--rg-entry-offset", `${Math.round((1 - entry) * 95)}px`);
   // Keep the title pinned until the next section has completely covered it.
   el.classList.toggle("rg-text-viewport-pinned", rect.top <= 0 && rect.bottom > 0);
 }, []);
 const scheduleEditorialMotion = useAnimationFrameCallback(updateEditorialMotion);
 const { ref: editorialRef } = useParallax({
   disabled: motionMode !== "desktop",
   onChange: scheduleEditorialMotion
 });
 useEffect(() => {
   const smallScreen = window.matchMedia("(max-width: 768px)");
   const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
   const update = () => {
     const mode = getEditorialMotionMode();
     setMotionMode(mode);
     if (mode !== "desktop") editorialRef.current?.classList.remove("rg-text-viewport-pinned");
   };
   smallScreen.addEventListener("change", update);
   reducedMotion.addEventListener("change", update);
   return () => {
     smallScreen.removeEventListener("change", update);
     reducedMotion.removeEventListener("change", update);
   };
 }, [editorialRef]);
 const [activeSlide, setActiveSlide] = useState(0);
 const [readySlides, setReadySlides] = useState({});
 const [preparedSlide, setPreparedSlide] = useState(0);
 const nextSlide = (activeSlide + 1) % slideImages.length;
 useEffect(() => {
   if (window.location.hash !== '#work') return;
   const frame = window.requestAnimationFrame(() => editorialRef.current?.scrollIntoView({ behavior: 'instant' }));
   return () => window.cancelAnimationFrame(frame);
 }, [editorialRef]);
 useEffect(() => {
   if (!readySlides[activeSlide] || readySlides[nextSlide]) return;
   // Give the opening image and fonts time to settle before fetching another photo.
   const timer = window.setTimeout(() => setPreparedSlide(nextSlide), 3000);
   return () => window.clearTimeout(timer);
 }, [activeSlide, nextSlide, readySlides]);
 useEffect(() => {
   // Let the next photo finish loading before starting its transition.
   if (!readySlides[activeSlide] || !readySlides[nextSlide]) return;
   const timer = window.setTimeout(() => setActiveSlide(nextSlide), 5500);
   return () => window.clearTimeout(timer);
 }, [activeSlide, nextSlide, readySlides]);
 return <>
  <main className="rg-page rg-editorial-home">
   <Helmet><title>Resham Gurung | Professional Photo Retouching</title><meta name="description" content="Elegant, natural photo retouching and editing for photographers, studios, real estate and brands."/></Helmet>
   <section className="rg-editorial-hero" aria-label="Professional photo retouching studio">
    <div className="rg-editorial-slideshow" aria-label="Featured photo retouching work">
     {slideImages.map((slide, i) => <div
      key={slide.name}
      className={`rg-editorial-slide${activeSlide === i ? " is-active" : ""}`}
      style={{ "--banner-position": slide.position, "--banner-mobile-position": slide.mobilePosition || slide.position }}
      aria-hidden={activeSlide !== i}
     >
      {/* Load the opening photo first, then prepare only the next unseen slide. */}
      {(i === activeSlide || readySlides[i] || i === preparedSlide) && <picture>
       <source media="(max-width: 540px)" type="image/avif" srcSet={bannerImages[slide.name].mobileAvif.srcSet} sizes="(max-width: 377px) 377px, 100vw" />
       <source media="(max-width: 540px)" srcSet={bannerImages[slide.name].mobile.srcSet} sizes="(max-width: 377px) 377px, 100vw" />
       <source type="image/avif" srcSet={bannerImages[slide.name].desktopAvif.srcSet} sizes="100vw" />
       <img
       {...bannerImages[slide.name].desktop}
       sizes="100vw"
       alt={slide.alt}
       decoding="async"
       fetchpriority={i === activeSlide ? "high" : "low"}
       onLoad={() => setReadySlides(ready => ready[i] ? ready : { ...ready, [i]: true })}
      /></picture>}
     </div>)}
</div>
   </section>
   <section className="rg-editorial-intro rg-container">
    <p className="rg-editorial-overline">THE ART OF PHOTO RETOUCHING</p>
    <h1>Every Detail Matters.</h1>
    <p>Thoughtful photo editing and natural, refined retouching for photographers, studios and creative businesses.</p>
    <div className="rg-editorial-signature">Beautifully edited. Naturally preserved.</div>
   </section>
   <section id="work" className="rg-scroll-story rg-willow-story" ref={editorialRef} aria-label="The art of photo retouching">
    <div className="rg-scroll-story-stage">
     {workCategories.map((category, index) => <CollagePhoto key={category.slug} category={category} index={index} mobileMotion={motionMode === "mobile"} />)}
     <div className="rg-scroll-story-center">
      <p className="rg-scroll-story-eyebrow">A CLOSER LOOK</p>
      <h2>Beyond the <em>Ordinary.</em></h2>
      <p className="rg-scroll-story-script">Beautifully refined.</p>
      <p className="rg-scroll-story-desc">Photographer by hobby. Retoucher by profession.</p>
      <Link className="rg-scroll-story-link" to="/services">Explore my services</Link>
     </div>
    </div>
   </section>
   <section className="rg-editorial-following" aria-label="Explore the studio">
    <div className="rg-editorial-three rg-container">
    <article><h2>My expertise</h2><p>Explore all 22 professional photo editing services.</p><Link className="rg-editorial-tile-arrow" to="/services">Discover my services →</Link></article>
    <article><h2>My Story</h2><p>Learn about my approach to careful, consistent editing.</p><Link className="rg-editorial-tile-arrow" to="/about">Get to know me →</Link></article>
    <article><h2>Let's Connect</h2><p>Tell us about your project or request a sample edit.</p><Link className="rg-editorial-tile-arrow" to="/contacts">Get in touch →</Link></article>
    </div>
   </section>
  </main>
  <Testimonials2/>
  <section className="rg-page rg-editorial-end rg-editorial-end-centered"><div className="rg-container"><p className="rg-editorial-overline">LET'S CREATE SOMETHING BEAUTIFUL</p><h2>Ready to elevate your images?</h2><p>Tell us what you're working on. I'd love to hear from you.</p><div className="rg-editorial-end-actions"><Link to="/contacts?request=quote">Request a Quote</Link><Link to="/contacts?request=sample">Request a Sample Edit</Link></div></div></section>
 </>;
}
